import React, { useState, useEffect, useMemo } from 'react';
import { X, Download, Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';
import Modal from './Modal';

const ExportConfigModal = ({
    isOpen,
    onClose,
    result,
    appliedRenames,
    addToast
}) => {
    const [includeAll, setIncludeAll] = useState(false);
    const [filename, setFilename] = useState('');
    const [showFullPreview, setShowFullPreview] = useState(false);
    const [copied, setCopied] = useState(false);

    // Generate short timestamp-based filename
    useEffect(() => {
        if (isOpen && result) {
            const now = new Date();
            const timestamp = now.toISOString().slice(0, 16).replace('T', '_').replace(':', '');
            setFilename(`rename_config_${timestamp}.json`);
        }
    }, [isOpen, result]);

    // Generate config data
    const config = useMemo(() => {
        if (!result?.diff?.suggested_renames) return null;

        const renames = result.diff.suggested_renames;
        const mappings = includeAll
            ? renames
            : renames.filter((_, i) => appliedRenames.has(i));

        return {
            version: "1.0",
            exported_at: new Date().toISOString(),
            mappings: mappings.map((rename) => ({
                from: rename.old_field,
                to: rename.new_field,
                confidence: Math.round(rename.confidence * 100) + '%'
            })),
            stats: {
                total: renames.length,
                applied: appliedRenames.size,
                exporting: includeAll ? renames.length : appliedRenames.size
            }
        };
    }, [result, appliedRenames, includeAll]);

    const handleDownload = () => {
        if (!config) return;

        const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || 'rename_config.json';
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 150);

        addToast?.('Config downloaded!', 'success');
        onClose();
    };

    const handleCopy = async () => {
        if (!config) return;
        try {
            await navigator.clipboard.writeText(JSON.stringify(config, null, 2));
            setCopied(true);
            addToast?.('Copied to clipboard!', 'success');
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            addToast?.('Failed to copy', 'error');
        }
    };

    // Preview JSON with basic formatting
    const previewJSON = useMemo(() => {
        if (!config) return '';
        const json = JSON.stringify(config, null, 2);
        const lines = json.split('\n');
        return showFullPreview ? json : lines.slice(0, 12).join('\n') + (lines.length > 12 ? '\n...' : '');
    }, [config, showFullPreview]);

    if (!result?.diff?.suggested_renames) return null;

    const appliedCount = appliedRenames.size;
    const totalCount = result.diff.suggested_renames.length;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Export Rename Config"
            maxWidth="max-w-lg"
            footer={
                <div className="flex items-center gap-2">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-300 hover:bg-[#3c3c3c] rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleCopy}
                        className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                        {copied ? <Check size={16} /> : <Copy size={16} />}
                        {copied ? 'Copied!' : 'Copy'}
                    </button>
                    <button
                        onClick={handleDownload}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                        <Download size={16} />
                        Download
                    </button>
                </div>
            }
        >
            <div className="space-y-4">
                {/* Stats Row */}
                <div className="flex items-center gap-4 p-3 bg-[#1e1e1e] rounded-lg">
                    <div className="text-center flex-1">
                        <div className="text-xl font-bold text-white">{totalCount}</div>
                        <div className="text-xs text-gray-500">Total</div>
                    </div>
                    <div className="text-center flex-1">
                        <div className="text-xl font-bold text-green-600">{appliedCount}</div>
                        <div className="text-xs text-gray-500">Applied</div>
                    </div>
                    <div className="text-center flex-1 border-l border-gray-600 pl-4">
                        <div className="text-xl font-bold text-blue-600">{includeAll ? totalCount : appliedCount}</div>
                        <div className="text-xs text-gray-500">Exporting</div>
                    </div>
                </div>

                {/* Toggle */}
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setIncludeAll(false)}
                        className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${!includeAll
                            ? 'bg-blue-600 text-white'
                            : 'bg-[#3c3c3c] text-[#cccccc]'
                            }`}
                    >
                        Applied Only ({appliedCount})
                    </button>
                    <button
                        onClick={() => setIncludeAll(true)}
                        className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${includeAll
                            ? 'bg-blue-600 text-white'
                            : 'bg-[#3c3c3c] text-[#cccccc]'
                            }`}
                    >
                        All Changes ({totalCount})
                    </button>
                </div>

                {/* Filename */}
                <div>
                    <label className="block text-xs font-medium text-[#808080] mb-1">
                        Filename
                    </label>
                    <div className="relative">
                        <input
                            type="text"
                            value={filename}
                            onChange={(e) => setFilename(e.target.value)}
                            className="w-full px-3 py-2 pr-8 text-sm border-[#4c4c4c] rounded-lg bg-[#252526] text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="rename_config.json"
                        />
                        {filename && (
                            <button
                                onClick={() => setFilename('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Preview */}
                <div>
                    <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-[#808080]">
                            Preview
                        </label>
                        <button
                            onClick={() => setShowFullPreview(!showFullPreview)}
                            className="text-xs text-blue-400 hover:underline flex items-center gap-1"
                        >
                            {showFullPreview ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                            {showFullPreview ? 'Less' : 'More'}
                        </button>
                    </div>
                    <div className="bg-[#1e1e1e] rounded-lg p-3 max-h-48 overflow-auto">
                        <pre className="text-xs font-mono text-gray-100 whitespace-pre-wrap">
                            {previewJSON}
                        </pre>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ExportConfigModal;
